/**
 * Central de som das notificações — canal único com prioridade.
 *
 * Regras:
 *  1. Só existe UM som tocando por vez. Nunca dois avisos sonoros juntos.
 *  2. Ganha o canal quem tem a maior prioridade (alarme > sino > toque leve).
 *     Empate: quem chegou primeiro. Os outros ficam "em espera" (sem som) e
 *     assumem o canal quando a ocorrência que estava soando é resolvida.
 *  3. Avisos de um toque só (`playOnce`, ex: nova mensagem) esperam a vez:
 *     se um alarme persistente está ativo, o toque leve só sai depois que ele
 *     for resolvido.
 *  4. Persistente repete com intervalo configurável (padrão 8s; 0 = contínuo).
 *  5. `snooze(ms)` silencia tudo temporariamente sem resolver as ocorrências.
 *
 * Navegadores só liberam áudio depois do primeiro gesto do usuário na aba.
 * Quando o toque é bloqueado, `blocked` fica true, a UI mostra o aviso e o
 * primeiro clique destrava o que estava pendente.
 */
export const SOUND_SRC = {
  none: null,
  ping: "/sounds/notify_ping.wav",
  marimba: "/sounds/notify_marimba.wav",
  bell: "/sounds/chat_alert.wav",
  chime: "/sounds/notify_chime.wav",
  tritone: "/sounds/notify_tritone.wav",
  beep: "/sounds/notify_beep.wav",
  radar: "/sounds/notify_radar.wav",
  buzz: "/sounds/notify_buzz.wav",
  gong: "/sounds/notify_gong.wav",
  siren: "/sounds/notify_siren.wav",
  alarm: "/sounds/notify_alarm.wav",
  // Sons fortes — ambientes barulhentos.
  klaxon: "/sounds/notify_klaxon.wav",
  airhorn: "/sounds/notify_airhorn.wav",
  evacuation: "/sounds/notify_evacuation.wav",
  emergency: "/sounds/notify_emergency.wav",
  doorbell_loud: "/sounds/notify_doorbell_loud.wav",
  backup_beep: "/sounds/notify_backup_beep.wav",
};

export const SOUND_PRIORITY = {
  alarm: 3,
  siren: 3,
  buzz: 3,
  gong: 3,
  klaxon: 3,
  airhorn: 3,
  evacuation: 3,
  emergency: 3,
  doorbell_loud: 3,
  backup_beep: 3,
  bell: 2,
  beep: 2,
  radar: 2,
  tritone: 2,
  chime: 1,
  ping: 1,
  marimba: 1,
  none: 0,
};

export const REPEAT_OPTIONS = [
  { ms: 0, label: "Contínuo (sem pausa)" },
  { ms: 4000, label: "A cada 4 segundos" },
  { ms: 8000, label: "A cada 8 segundos" },
  { ms: 15000, label: "A cada 15 segundos" },
  { ms: 30000, label: "A cada 30 segundos" },
];

export const SNOOZE_OPTIONS = [
  { ms: 5 * 60000, label: "5 minutos" },
  { ms: 15 * 60000, label: "15 minutos" },
  { ms: 30 * 60000, label: "30 minutos" },
  { ms: 60 * 60000, label: "1 hora" },
  { ms: 6 * 60 * 60000, label: "6 horas" },
  { ms: 24 * 60 * 60000, label: "24 horas" },
];

export const VOLUME_OPTIONS = [
  { gain: 1, label: "Normal", desc: "Volume padrão" },
  { gain: 1.8, label: "Alto", desc: "Sala movimentada" },
  { gain: 3, label: "Muito alto", desc: "Oficina / pátio" },
  { gain: 5, label: "Máximo", desc: "Ambiente barulhento" },
];

const DEFAULT_REPEAT_MS = 8000;
const REPEAT_STORAGE_KEY = "notif.sound.repeatMs";
const SNOOZE_STORAGE_KEY = "notif.sound.snoozeUntil";
const VOLUME_STORAGE_KEY = "notif.sound.gain";
const ONE_SHOT_DEBOUNCE_MS = 600;

const active = new Map(); // key → { soundId, at }
const stateListeners = new Set();

let current = null; // { key, soundId, audio, timer }
let pendingOnce = null; // soundId aguardando o canal liberar
let blocked = false;
let lastOneShotAt = 0;
let gestureHooked = false;
let snoozeTimer = null;

function readStore(key, fallback) {
  if (typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(key);
  if (raw === null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

let repeatMs = readStore(REPEAT_STORAGE_KEY, DEFAULT_REPEAT_MS);
let snoozeUntil = readStore(SNOOZE_STORAGE_KEY, 0);
let gain = readStore(VOLUME_STORAGE_KEY, 1) || 1;

export function getGain() {
  return gain;
}

export function setGain(v) {
  gain = Number(v) || 1;
  if (typeof window !== "undefined")
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(gain));
  if (current) {
    // aplica na hora no som que já está tocando
    aplicarVolume(current.audio);
  }
  emit();
}

/* Amplificação acima de 100%: o <audio> só vai até volume 1, então quando o
 * usuário pede "muito alto"/"máximo" o áudio é roteado pelo Web Audio com um
 * GainNode. Cada elemento só pode ser conectado uma vez. */
let ctx = null;
const roteados = new WeakSet();

function aplicarVolume(audio) {
  const base = audio.loop ? 0.5 : 0.6;
  if (gain <= 1) {
    audio.volume = Math.min(1, base * gain);
    return;
  }
  audio.volume = 1;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    if (!roteados.has(audio)) {
      const fonte = ctx.createMediaElementSource(audio);
      const g = ctx.createGain();
      fonte.connect(g).connect(ctx.destination);
      audio._gainNode = g;
      roteados.add(audio);
    }
    if (audio._gainNode) audio._gainNode.gain.value = gain;
  } catch {
    /* sem Web Audio: fica no volume máximo do elemento */
  }
}

export function getAudioState() {
  return {
    blocked,
    playingKey: current?.key || null,
    playingSound: current?.soundId || null,
    activeCount: active.size,
    waitingCount: Math.max(0, active.size - (current ? 1 : 0)),
    snoozeUntil,
    repeatMs,
    gain,
    isLeader: isAudioLeader(),
  };
}

function emit() {
  const snap = getAudioState();
  stateListeners.forEach((fn) => {
    try {
      fn(snap);
    } catch {
      /* noop */
    }
  });
}

function setBlocked(v) {
  if (blocked === v) return;
  blocked = v;
  emit();
}

function hookGesture() {
  if (gestureHooked || typeof window === "undefined") return;
  gestureHooked = true;
  const onGesture = () => {
    gestureHooked = false;
    setBlocked(false);
    reevaluate(); // retoma o som persistente que ficou travado
    window.removeEventListener("pointerdown", onGesture);
    window.removeEventListener("keydown", onGesture);
  };
  window.addEventListener("pointerdown", onGesture, { once: true });
  window.addEventListener("keydown", onGesture, { once: true });
}

function makeAudio(src, loop) {
  const a = new Audio(src);
  a.preload = "auto";
  a.loop = loop;
  aplicarVolume(a);
  return a;
}

function tryPlay(audio, force = false) {
  if (!force && !isAudioLeader()) return;
  try {
    const p = audio.play();
    if (p && p.catch)
      p.catch(() => {
        setBlocked(true);
        hookGesture();
      });
  } catch {
    setBlocked(true);
    hookGesture();
  }
}

/* ------------------------------------------- aba dona do som (1 por PC) */

const LEADER_KEY = "notif.sound.leader";
const LEADER_TTL_MS = 4000;
const LEADER_BEAT_MS = 1500;
/** Aba do Painel Logístico é a dona preferencial do som. */
const PRIORITY_PATHS = ["/services/logistics"];
const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
let leaderTimer = null;

function tabRank() {
  const priority = PRIORITY_PATHS.some((p) =>
    window.location.pathname.startsWith(p),
  )
    ? 2
    : 0;
  return priority + (document.visibilityState === "visible" ? 1 : 0);
}

function readLeader() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LEADER_KEY);
    if (!raw) return null;
    const l = JSON.parse(raw);
    return l && l.id ? l : null;
  } catch {
    return null;
  }
}

function writeLeader() {
  try {
    window.localStorage.setItem(
      LEADER_KEY,
      JSON.stringify({
        id: tabId,
        ts: Date.now(),
        visible: document.visibilityState === "visible",
        rank: tabRank(),
      }),
    );
  } catch {
    /* noop */
  }
}

export function isAudioLeader() {
  if (typeof window === "undefined") return false;
  const l = readLeader();
  return !!l && l.id === tabId && Date.now() - l.ts < LEADER_TTL_MS;
}

/** Assume o som se ninguém respondeu ou se a dona atual está em segundo plano. */
let lastLeaderFlag = null;

function claimLeadership() {
  if (typeof window === "undefined") return;
  const l = readLeader();
  const stale = !l || Date.now() - l.ts > LEADER_TTL_MS;
  const mine = !!l && l.id === tabId;
  const mineRank = tabRank();
  const theirRank = l
    ? (typeof l.rank === "number" ? l.rank : l.visible ? 1 : 0)
    : -1;
  if (stale || mine || mineRank > theirRank) {
    const wasLeader = mine && !stale;
    writeLeader();
    if (!wasLeader) {
      // ao assumir, reabre o canal para o som realmente sair nesta aba
      stopChannel();
      reevaluate();
    }
  } else if (current) {
    // outra aba assumiu o som: para o áudio local sem perder o estado
    stopChannel();
    emit();
  }
  const flag = isAudioLeader();
  if (flag !== lastLeaderFlag) {
    lastLeaderFlag = flag;
    emit();
  }
}

if (typeof window !== "undefined") {
  claimLeadership();
  leaderTimer = setInterval(claimLeadership, LEADER_BEAT_MS);
  document.addEventListener("visibilitychange", claimLeadership);
  window.addEventListener("pagehide", () => {
    if (leaderTimer) clearInterval(leaderTimer);
    const l = readLeader();
    if (l && l.id === tabId) {
      try {
        window.localStorage.removeItem(LEADER_KEY);
      } catch {
        /* noop */
      }
    }
  });
}

/* ---------------------------------------------------------------- snooze */

export function isSnoozed() {
  return snoozeUntil > Date.now();
}

function armSnoozeTimer() {
  if (snoozeTimer) clearTimeout(snoozeTimer);
  snoozeTimer = null;
  if (!isSnoozed()) return;
  snoozeTimer = setTimeout(
    () => {
      snoozeTimer = null;
      snoozeUntil = 0;
      persistSnooze();
      reevaluate();
    },
    snoozeUntil - Date.now() + 250,
  );
}

function persistSnooze() {
  if (typeof window === "undefined") return;
  if (snoozeUntil) window.localStorage.setItem(SNOOZE_STORAGE_KEY, String(snoozeUntil));
  else window.localStorage.removeItem(SNOOZE_STORAGE_KEY);
}

export function snooze(ms) {
  snoozeUntil = Date.now() + ms;
  persistSnooze();
  armSnoozeTimer();
  reevaluate();
}

/** Aplica o silenciamento que veio do servidor (fonte da verdade).
 *
 * O `localStorage` continua sendo usado como cache local, só para o som não
 * voltar a tocar no intervalo entre o carregamento da página e a resposta da
 * API — quem manda é sempre o estado salvo no usuário.
 */
export function syncSnoozeUntil(ts) {
  const alvo = Number(ts) || 0;
  if (alvo === snoozeUntil) return;
  snoozeUntil = alvo;
  persistSnooze();
  armSnoozeTimer();
  reevaluate();
  emit();
}

export function cancelSnooze() {
  snoozeUntil = 0;
  persistSnooze();
  armSnoozeTimer();
  reevaluate();
}

if (typeof window !== "undefined" && isSnoozed()) armSnoozeTimer();

// Reforço entre abas do mesmo navegador: o valor em memória só era lido no
// carregamento, então uma aba já aberta continuava tocando depois de alguém
// silenciar em outra.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== SNOOZE_STORAGE_KEY) return;
    syncSnoozeUntil(Number(e.newValue) || 0);
  });
}

/* ------------------------------------------------------- ritmo do loop */

export function getRepeatMs() {
  return repeatMs;
}

export function setRepeatMs(ms) {
  repeatMs = Number(ms) || 0;
  if (typeof window !== "undefined")
    window.localStorage.setItem(REPEAT_STORAGE_KEY, String(repeatMs));
  if (current) {
    // aplica o novo ritmo imediatamente reabrindo o canal
    const { key, soundId } = current;
    stopChannel();
    startChannel({ key, soundId });
  }
  emit();
}

/* ------------------------------------------------------------- canal */

function stopChannel() {
  if (!current) return;
  if (current.timer) clearTimeout(current.timer);
  try {
    current.audio.onended = null;
    current.audio.pause();
    current.audio.currentTime = 0;
  } catch {
    /* noop */
  }
  current = null;
}

function startChannel({ key, soundId }) {
  const src = SOUND_SRC[soundId];
  if (!src) return;
  const audio = makeAudio(src, repeatMs === 0);
  current = { key, soundId, audio, timer: null };
  if (repeatMs > 0) {
    audio.onended = () => {
      if (!current || current.audio !== audio) return;
      current.timer = setTimeout(() => {
        if (!current || current.audio !== audio) return;
        if (isSnoozed()) return;
        try {
          audio.currentTime = 0;
        } catch {
          /* noop */
        }
        tryPlay(audio);
      }, repeatMs);
    };
  }
  tryPlay(audio);
}

function pickWinner() {
  let best = null;
  active.forEach((v, key) => {
    const p = SOUND_PRIORITY[v.soundId] || 0;
    if (!p) return;
    if (!best || p > best.p || (p === best.p && v.at < best.at))
      best = { key, soundId: v.soundId, p, at: v.at };
  });
  return best;
}

function fireOnce(soundId) {
  const src = SOUND_SRC[soundId];
  if (!src) return;
  const now = Date.now();
  if (now - lastOneShotAt < ONE_SHOT_DEBOUNCE_MS) return;
  lastOneShotAt = now;
  tryPlay(makeAudio(src, false));
}

function reevaluate() {
  if (isSnoozed()) {
    stopChannel();
    emit();
    return;
  }
  const winner = pickWinner();
  if (winner) {
    if (!current || current.key !== winner.key) {
      stopChannel();
      startChannel(winner);
    }
  } else {
    stopChannel();
    if (pendingOnce) {
      const s = pendingOnce;
      pendingOnce = null;
      fireOnce(s);
    }
  }
  emit();
}

/* ------------------------------------------------------------- API */

/** Aviso de um toque só. Espera a vez se algum persistente estiver ativo. */
export function playOnce(soundId) {
  if (!soundId || soundId === "none" || typeof window === "undefined") return;
  if (active.size > 0 || current || isSnoozed()) {
    const cur = SOUND_PRIORITY[pendingOnce] || 0;
    if ((SOUND_PRIORITY[soundId] || 0) >= cur) pendingOnce = soundId;
    emit();
    return;
  }
  fireOnce(soundId);
}

/** Registra uma ocorrência que deve soar até ser resolvida. */
export function sustain(key, soundId) {
  if (!key || !soundId || soundId === "none" || typeof window === "undefined") return;
  const prev = active.get(key);
  if (prev && prev.soundId === soundId) return;
  active.set(key, { soundId, at: prev?.at ?? Date.now() });
  reevaluate();
}

/** Ocorrência resolvida: libera o canal para a próxima da fila. */
export function release(key) {
  if (!active.has(key)) return;
  active.delete(key);
  if (current?.key === key) stopChannel();
  reevaluate();
}

export function releaseAll() {
  active.clear();
  stopChannel();
  reevaluate();
}

/** Mantém soando SÓ as ocorrências que o servidor ainda considera abertas.
 *
 * Sem isso, uma aba que perdeu o WebSocket segue com o alarme travado na
 * memória mesmo depois de a ocorrência ter sido resolvida em outro lugar.
 */
export function syncSustained(keys) {
  const keep = new Set(keys || []);
  let changed = false;
  active.forEach((_v, k) => {
    if (keep.has(k)) return;
    active.delete(k);
    if (current?.key === k) stopChannel();
    changed = true;
  });
  if (changed) reevaluate();
}

export function isBlocked() {
  return blocked;
}

export function subscribeAudioState(fn) {
  stateListeners.add(fn);
  fn(getAudioState());
  return () => stateListeners.delete(fn);
}

/** Pré-escuta usada na tela de configurações (ignora fila, snooze e liderança). */
export function preview(soundId) {
  const src = SOUND_SRC[soundId];
  if (!src) return;
  lastOneShotAt = Date.now();
  tryPlay(makeAudio(src, false), true);
}
